import type { IconName } from '../icons';
import { canViewPath } from '../../services/capabilities';

// The single source of truth for primary navigation. Both nav surfaces
// — the desktop top nav and the phone bottom tab bar — read this one list, so a
// route is added once and appears in the right place(s). The two surfaces show
// curated subsets, not the same set: the top nav carries the fuller menu (incl.
// account settings), while the tab bar is the thumb-reachable destinations.
export interface NavItem {
  href: string;
  label: string; // top-nav label
  tabLabel?: string; // tab-bar label when it differs from `label`
  icon?: IconName; // tab-bar glyph (required for tab-bar items)
  inTopNav?: boolean; // default true
  inTabBar?: boolean; // default false
}

export const NAV_ITEMS: NavItem[] = [
  { href: '/', label: 'Home', icon: 'home', inTabBar: true },
  { href: '/products', label: 'Products', tabLabel: 'Shop', icon: 'cases', inTabBar: true },
  { href: '/contact', label: 'Contact', icon: 'compass', inTabBar: true },
  // The signed-in user's hub — "Account" in both the desktop top nav and the
  // phone tab bar, so there is always a normal way in. Customers manage orders,
  // addresses and settings there; staff additionally get the back-office entry
  // (the Admin row) inside it.
  { href: '/me', label: 'Account', icon: 'me', inTabBar: true },
];

// Prefix-aware active matching so a tab stays lit on its sub-pages — e.g. Me on
// /settings and /settings/devices (reached from the Me hub), Orders on a future
// /orders/:id. '/' matches only itself.
export function isActiveTab(href: string, pathname: string = location.pathname): boolean {
  if (pathname === href) return true;
  if (href === '/') return false;
  if (pathname.startsWith(href + '/')) return true;
  return href === '/me' && (pathname === '/settings' || pathname.startsWith('/settings/'));
}

// The nav surfaces drop any target this user's role + device class can't reach
// (client mirror); open routes (home, account settings) always pass.
export function topNavItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inTopNav !== false && canViewPath(i.href));
}

export function tabBarItems(): NavItem[] {
  return NAV_ITEMS.filter((i) => i.inTabBar === true && canViewPath(i.href));
}
